import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Provider from 'provider';
import Analytics from 'analytics';
import { EVENTS } from 'analytics/events';
import logging from 'utils/logging';

const log = logging.getLogger('CancelDialog');

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    border: '2px solid rgba(46,196,182,0.25)',
    boxShadow: '0 16px 36px rgba(46,196,182,0.18)',
  },
});

const Title = styled(DialogTitle)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '1.6rem',
  letterSpacing: '0.02em',
  color: '#222',
  paddingBottom: 8,
});

const Body = styled(Typography)({
  fontSize: '0.95rem',
  color: '#555',
  lineHeight: 1.5,
});

// Matches the Play Now CTA on the done screen, scaled smaller for the dialog.
const ConfirmButton = styled(Button)({
  padding: '10px 20px',
  background: 'linear-gradient(135deg, #2ec4b6 0%, #1a7a72 100%)',
  color: '#fff',
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '0.85rem',
  letterSpacing: '0.18em',
  fontWeight: 800,
  textTransform: 'uppercase',
  borderRadius: 10,
  boxShadow: '0 6px 14px rgba(46,196,182,0.3)',
  minWidth: 140,
  '&:hover': {
    background: 'linear-gradient(135deg, #2ec4b6 0%, #1a7a72 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 8px 18px rgba(46,196,182,0.4)',
  },
  '&.Mui-disabled': {
    background: 'linear-gradient(135deg, #2ec4b6 0%, #1a7a72 100%)',
    color: '#fff',
    opacity: 0.75,
    boxShadow: 'none',
  },
});

const KeepButton = styled(Button)({
  padding: '10px 16px',
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '0.8rem',
  letterSpacing: '0.18em',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: '#1a7a72',
  '&:hover': {
    background: 'rgba(46,196,182,0.08)',
  },
});

const CancelDialog = ({ open, onClose, gameId, phaseAtCancel, elapsedSeconds }) => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  // Reset submitting state when the dialog re-opens
  useEffect(() => {
    if (open) setSubmitting(false);
  }, [open]);

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    Analytics.track(EVENTS.GAME_CREATE_CANCELED, {
      game_id: gameId,
      phase_at_cancel: phaseAtCancel,
      elapsed_seconds: elapsedSeconds,
      cancel_method: 'dialog',
    });
    try {
      await Provider.deleteGame(gameId);
    } catch (err) {
      log.warn('deleteGame failed during cancel', err);
    }
    onClose();
    navigate('/manage');
  };

  const handleClose = (...args) => {
    // Block the backdrop / escape close while a delete is in flight so we
    // don't leave the dialog half-mounted while the request is still racing.
    if (submitting) return;
    onClose(...args);
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <Title>Cancel this game?</Title>
      <DialogContent>
        <Body>Any progress will be deleted permanently. This can't be undone.</Body>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1.5 }}>
        <KeepButton onClick={handleClose} disabled={submitting}>
          Keep Building
        </KeepButton>
        <ConfirmButton
          onClick={handleConfirm}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
        >
          {submitting ? 'Cancelling…' : 'Yes, Cancel'}
        </ConfirmButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default CancelDialog;
